import { FileTextIcon } from 'core/components/Icons'
import React from 'react'

export interface Column<T> {
  key: string
  label: string | React.ReactNode
  render?: (row: T) => React.ReactNode
  sortable?: boolean
  className?: string
}

interface TableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  sortKey?: string
  sortOrder?: 'asc' | 'desc'
  onSort?: (key: string) => void
  pagination?: {
    currentPage: number
    totalPages: number
    onPageChange: (page: number) => void
    totalItems?: number
    itemsPerPage?: number
  }
  emptyMessage?: string
  emptyIcon?: React.ReactNode
  /** Fixes the table body at this height (e.g. '420px') — content scrolls if it overflows, instead of the table growing or stretching to match a sibling. */
  bodyHeight?: string
  /** Pins the last row in `data` (e.g. a TOTAL row) to the bottom of the scrollable body instead of scrolling with the rest. */
  stickyLastRow?: boolean
}

const SKELETON_ROWS = ['skeleton-row-1', 'skeleton-row-2', 'skeleton-row-3', 'skeleton-row-4', 'skeleton-row-5']

function getColumnAlignment(className?: string): { isCentered: boolean; isRight: boolean } {
  const isCentered = Boolean(className?.includes('text-center') || className?.includes('justify-center'))
  const isRight = Boolean(className?.includes('text-right') || className?.includes('justify-end'))
  return { isCentered, isRight }
}

function getSkeletonAlignmentClass(className?: string): string {
  const { isCentered, isRight } = getColumnAlignment(className)
  if (isCentered) {
    return 'mx-auto'
  }

  if (isRight) {
    return 'ml-auto'
  }

  return ''
}

function getHeaderJustifyClass(className?: string): string {
  const { isCentered, isRight } = getColumnAlignment(className)
  if (isCentered) {
    return 'justify-center'
  }

  if (isRight) {
    return 'justify-end'
  }

  return 'justify-start'
}

function getRowKey<T>(row: T, index: number): string | number {
  const identifiedRow = row as { id?: string | number; key?: string | number } | null
  return identifiedRow?.id ?? identifiedRow?.key ?? `row-${index}`
}

function TableSkeletonRows<T>({ columns }: { columns: Column<T>[] }) {
  return (
    <>
      {SKELETON_ROWS.map((rowKey) => (
        <tr key={rowKey} className="animate-pulse">
          {columns.map((col) => (
            <td key={col.key} className={`px-6 py-4 ${col.className || ''}`}>
              <div className={`h-4 bg-border rounded w-2/3 ${getSkeletonAlignmentClass(col.className)}`} />
            </td>
          ))}
        </tr>
      ))}
    </>
  )
}

function TableEmptyRow({
  colSpan,
  message,
  emptyIcon,
}: {
  colSpan: number
  message: string
  emptyIcon?: React.ReactNode
}) {
  const iconToRender = emptyIcon === undefined ? <FileTextIcon size={20} className="opacity-60" /> : emptyIcon

  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-8 text-center text-text-secondary">
        <div className="flex flex-col items-center justify-center">
          {iconToRender && (
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-bg text-text-secondary">
              {iconToRender}
            </div>
          )}
          <span className="text-xs font-semibold text-text-primary">{message}</span>
        </div>
      </td>
    </tr>
  )
}

export function Table<T>({
  columns,
  data,
  loading = false,
  sortKey,
  sortOrder,
  onSort,
  pagination,
  emptyMessage = 'No records found.',
  emptyIcon,
  bodyHeight,
  stickyLastRow = false,
}: TableProps<T>) {
  const handleSortClick = (column: Column<T>) => {
    if (column.sortable && onSort) {
      onSort(column.key)
    }
  }

  const renderTableBody = () => {
    if (loading) {
      return <TableSkeletonRows columns={columns} />
    }

    if (data.length === 0) {
      return <TableEmptyRow colSpan={columns.length} message={emptyMessage} emptyIcon={emptyIcon} />
    }

    return data.map((row, rIdx) => {
      const isStickyFooter = stickyLastRow && rIdx === data.length - 1
      const rowKey = getRowKey(row, rIdx)
      const rowClassName = isStickyFooter
        ? 'sticky bottom-0 z-10 border-t-2 border-border bg-surface'
        : 'hover:bg-bg/50 transition-colors duration-150 ease-in-out'

      return (
        <tr key={rowKey} className={rowClassName}>
          {columns.map((column) => (
            <td key={column.key} className={`px-6 py-4 whitespace-nowrap ${column.className || ''}`}>
              {column.render ? column.render(row) : (row[column.key as keyof T] as unknown as React.ReactNode)}
            </td>
          ))}
        </tr>
      )
    })
  }

  return (
    <div className="flex flex-col w-full h-full bg-surface border border-border rounded-card overflow-hidden">
      <div
        className={`min-w-full align-middle overflow-x-auto ${bodyHeight ? 'flex-shrink-0 overflow-y-auto' : 'flex-1'}`}
        style={bodyHeight ? { height: bodyHeight } : undefined}
      >
        <table className="min-w-full divide-y divide-border table-auto text-left text-sm">
          <thead
            className={`bg-bg text-text-secondary text-xs uppercase tracking-wider font-semibold select-none ${
              bodyHeight ? 'sticky top-0 z-10' : ''
            }`}
          >
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => handleSortClick(column)}
                  className={`px-6 py-4 ${
                    column.sortable ? 'cursor-pointer hover:bg-border/30 hover:text-text-primary' : ''
                  } ${column.className || ''} transition-colors duration-150`}
                >
                  <div className={`flex items-center gap-1.5 ${getHeaderJustifyClass(column.className)}`}>
                    <span>{column.label}</span>
                    {column.sortable && sortKey === column.key && (
                      <span className="text-primary font-bold">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-surface text-text-primary">{renderTableBody()}</tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between border-t border-border px-6 py-4 bg-surface gap-3 text-xs text-text-secondary select-none">
          {/* Entry count info */}
          <div>
            {pagination.totalItems !== undefined && pagination.itemsPerPage !== undefined ? (
              <span>
                Showing{' '}
                <span className="font-bold text-text-primary">
                  {Math.min(pagination.totalItems, (pagination.currentPage - 1) * pagination.itemsPerPage + 1)}
                </span>{' '}
                to{' '}
                <span className="font-bold text-text-primary">
                  {Math.min(pagination.totalItems, pagination.currentPage * pagination.itemsPerPage)}
                </span>{' '}
                of <span className="font-bold text-text-primary">{pagination.totalItems}</span> entries
              </span>
            ) : (
              <span>
                Page <span className="font-bold text-text-primary">{pagination.currentPage}</span> of{' '}
                <span className="font-bold text-text-primary">{pagination.totalPages}</span>
              </span>
            )}
          </div>

          {/* Page buttons */}
          <div className="flex items-center gap-1.5">
            {/* Previous */}
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage <= 1 || loading}
              className="px-3 py-1.5 border border-border rounded-btn bg-card text-text-primary hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors duration-150"
            >
              Previous
            </button>

            {/* Page number buttons */}
            {Array.from({ length: pagination.totalPages }).map((_, pageIdx) => {
              const pageNum = pageIdx + 1
              if (
                pagination.totalPages > 5 &&
                Math.abs(pagination.currentPage - pageNum) > 1 &&
                pageNum !== 1 &&
                pageNum !== pagination.totalPages
              ) {
                if (pageNum === 2 || pageNum === pagination.totalPages - 1) {
                  return (
                    <span key={pageNum} className="px-1 text-text-secondary select-none">
                      ...
                    </span>
                  )
                }

                return null
              }

              const isActive = pagination.currentPage === pageNum
              return (
                <button
                  key={pageNum}
                  onClick={() => pagination.onPageChange(pageNum)}
                  disabled={loading}
                  className={`px-3 py-1.5 rounded-btn font-semibold text-center transition-colors duration-150 ${
                    isActive
                      ? 'bg-primary text-primary-fg shadow-sm'
                      : 'border border-border bg-card text-text-primary hover:bg-bg disabled:opacity-40'
                  }`}
                >
                  {pageNum}
                </button>
              )
            })}

            {/* Next */}
            <button
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage >= pagination.totalPages || loading}
              className="px-3 py-1.5 border border-border rounded-btn bg-card text-text-primary hover:bg-bg disabled:opacity-40 disabled:cursor-not-allowed font-medium transition-colors duration-150"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
